import { useState, useCallback } from 'react';

interface Nis2Selection {
  nis2_article: string;
  nis2_letter: string;
}

interface UseNis2SelectionProps {
  initialArticle?: string;
  initialLetter?: string;
  onSelectionChange?: (selection: Nis2Selection) => void;
}

interface UseNis2SelectionReturn {
  article: string;
  letter: string;
  handleParentChange: (value: string | string[]) => void;
  handleChildChange: (value: string | string[]) => void;
  setSelection: (selection: Partial<Nis2Selection>) => void;
}

/**
 * Custom hook for managing NIS2 Article & Letter selection with proper auto-selection behavior
 * 
 * Features:
 * - Auto-selects parent (article) when child (letter) is selected
 * - Only resets child when parent is manually changed (not auto-selected)
 * - Provides consistent behavior across all components
 */
export const useNis2Selection = ({
  initialArticle = '',
  initialLetter = '',
  onSelectionChange
}: UseNis2SelectionProps = {}): UseNis2SelectionReturn => {
  const [article, setArticle] = useState(initialArticle);
  const [letter, setLetter] = useState(initialLetter);

  // Notify parent component of changes
  const notifyChange = useCallback((newArticle: string, newLetter: string) => {
    if (onSelectionChange) {
      onSelectionChange({
        nis2_article: newArticle,
        nis2_letter: newLetter
      });
    }
  }, [onSelectionChange]);

  const handleParentChange = useCallback((value: string | string[]) => {
    const newArticle = Array.isArray(value) ? value[0] || '' : value;
    console.log('=== useNis2Selection handleParentChange ===');
    console.log('Previous article:', article);
    console.log('Previous letter:', letter);
    console.log('New article:', newArticle);
    
    setArticle(newArticle);
    
    // Use functional update to get the latest letter state
    setLetter(currentLetter => {
      console.log('Current letter in functional update:', currentLetter);
      notifyChange(newArticle, currentLetter);
      return currentLetter; // Keep current letter unchanged
    });
  }, [article, letter, notifyChange]);

  const handleChildChange = useCallback((value: string | string[]) => {
    const newLetter = Array.isArray(value) ? value[0] || '' : value;
    console.log('=== useNis2Selection handleChildChange ===');
    console.log('Previous article:', article);
    console.log('Previous letter:', letter);
    console.log('New letter:', newLetter);
    setLetter(newLetter);
    notifyChange(article, newLetter);
  }, [article, letter, notifyChange]);

  const setSelection = useCallback((selection: Partial<Nis2Selection>) => {
    const newArticle = selection.nis2_article !== undefined ? selection.nis2_article : article;
    const newLetter = selection.nis2_letter !== undefined ? selection.nis2_letter : letter;
    
    setArticle(newArticle);
    setLetter(newLetter);
    notifyChange(newArticle, newLetter);
  }, [article, letter, notifyChange]);

  return {
    article,
    letter,
    handleParentChange,
    handleChildChange,
    setSelection
  };
};
